import clsx from 'clsx'
import { replace } from 'connected-react-router'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import styled from 'styled-components'

import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { fetchPopularHotelFilters } from 'actions/OfferActions'
import { fetchSpecificPlaces, fetchDestinationImageId, fetchPlaceById } from 'actions/PlaceActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import {
  searchOfferListFilters,
  searchResultDealClick,
  searchResultImpressionCount,
  searchSessionDealClick,
} from 'analytics/eventDefinitions'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { addGTMEvent } from 'api/googleTagManager'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { SearchFormRef } from 'components/Search'
import type { EnabledFilters } from 'components/Search/type'
import { getAppliedFilterCount } from 'components/Search/utils'
import { saveSearchedPlaces } from 'lib/search/searchedPlaces'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  HOTEL_SEARCH_TYPES,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, CHARTER_PAGE_SLUG, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG, MAX_TRAVELLERS_FOR_MALDIVES_CHARTER, MALDIVES_CHARTER_SEARCH_PAGE_MIN_CHECK_IN_DATE, MALDIVES_CHARTER_SEARCH_PAGE_MAX_CHECK_IN_DATE } from 'constants/charterFlight'
import { isMaldivesLocation } from 'lib/offer/isMaldivesHotel'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_NAME,
  ANYWHERE_PLACE_ID,
  CUSTOMER_RATING_GTE,
} from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import {
  GLOBAL_SEARCH_INITIAL_STATE,
  GlobalSearchStateActions,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDestinationTags from 'hooks/Search/useDestinationTags'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import {
  createRecentPropertySearchObject,
  createSearchSessionKey,
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchKey,
  getSearchTypeFromFilters,
  getsScheduleVisibilitiesFilter, isStandardOccupancy,
} from 'lib/search/searchUtils'
import { shouldHideAnytimePrices } from 'lib/search/hideAnytimePrices'
import {
  deleteSearchParams,
  parseSearchString,
  setManySearchParamValues,
  setSearchParamValue,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validateLatLngSearchParams,
  validateNaturalLanguageSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import { addRecentSearch } from 'actions/SearchActions'
import * as Analytics from 'analytics/analytics'
import {
  mapGlobalSearchContextToSnowplowSearchEvent,
  mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext,
} from 'analytics/mapSnowplowSearchTracking'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import Heading from 'LuxKit/Typography/Heading'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { FlightsClassTypes } from 'constants/flight'
import config from 'constants/config'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ModalContext from 'contexts/ModalContext'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import type { TrackingOptions } from 'hooks/Offers/useOfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListExtras from 'hooks/Offers/useOfferListExtras'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOffers from 'hooks/Offers/useOffers'
import useOffersWithProperty from 'hooks/Offers/useOffersWithProperty'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import useQueryParams from 'hooks/useQueryParams'
import { without } from 'lib/array/arrayUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { isHotel, isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import HotelSearchHeader from './HotelSearchHeader'
import HotelSearchNoSearchResultSection from './HotelSearchNoSearchResultSection'
import MaldivesBanner from './MaldivesBanner'
import OfferCountAndSort from './OfferCountAndSort'
import SearchPageTrustIndicators from './SearchPageTrustIndicators'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { getSource } from 'selectors/offerSelectors'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { excludeNullOrUndefined } from 'checkout/utils'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import Group from '../../utils/Group'
import { useInView } from 'react-intersection-observer'
import OfferListLoadingOfferTile from '../../OfferList/OfferListTiles/OfferListLoadingOfferTile'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import useSortWasApplied from 'hooks/Search/useSortWasApplied'
import useFilterWasApplied from 'hooks/Search/useFilterWasApplied'
import useTrackingSearchEvent from 'hooks/Search/useTrackingSearchEvent'
import OfferSelectionStickyFooter from 'components/OfferComparison/OfferSelectionStickyFooter'
import uuidV4 from 'lib/string/uuidV4Utils'
import usePropertyTags from '../../../hooks/Offers/usePropertyTags'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import useSearchSession from '../../../hooks/Search/useSearchSession'
import { useSearchBlockingState } from 'hooks/Search/useSearchBlockingState'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { getAccommodationSearchHeadMeta } from './utils/getAccommodationSearchHeadMeta'
import { MaldivesBundleProvider } from './MaldivesBundleContext'
import { useNextValidCharterPeriod } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useNextValidCharterPeriod'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import usePrevious from '../../../hooks/usePrevious'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import { getDestinationSearchCanonicalPath } from './utils/getDestinationSearchCanonicalPath'

const destinationTypes = new Set(['destination', 'naturalLanguage'])
const recentSearchesWhitelist = new Set(['destination', 'landmark', 'property', 'naturalLanguage'])

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const HeadingWrapper = styled.div`
  padding-left: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding-left: 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)
  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function HotelSearchPage({
  filters,
  match,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  location,
  history,
  specificPlaces,
}: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const locationLabels = useLocationSearchLabels()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const currentPageHash = useAppSelector(state => state.router.location.hash)
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const typeaheadId = params.typeaheadId

  const { blockSearch } = useSearchBlockingState({
    extracting: params.extracting,
    typeaheadId,
    query: params.query,
    windowSearch,
    pathname: location.pathname,
  })

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const source = useAppSelector(state => getSource(state))
  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const destinationImageId = useAppSelector(state =>
    filters.destinationId ? state.destination.places[filters.destinationId]?.imageId : undefined,
  )

  const initialPage = useRef<number>(params.page ? parseInt(params.page) : 1)
  const naturalLanguageSearch = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)
  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'hotel' })

  // used to determine if the search was blocked by the typeahead extraction
  const previouslyBlockedSearch = usePrevious(params.extracting)

  const searchKey = getSearchKey(params)
  const { searchEvent: searchSessionSearchEvent } = useSearchEvent(searchKey)

  const searchSessionKey = createSearchSessionKey({
    destinationId: params.destinationId,
    propertyId: params.propertyId,
    landmarkId: params.landmarkId,
    lat: params.lat,
    lng: params.lng,
    query: params.query,
  })
  const { searchSessionId } = useSearchSession(searchSessionKey)

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.query || params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.query,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const destinationId = filters.destinationId
  const destinationPlace = useAppSelector(state => destinationId ? state.destination.places[destinationId] : undefined)
  const hasOfferTagsApplied = !!(filters.offerTags && filters.offerTags.length > 0)
  const { tags: destinationTags } = useDestinationTags(destinationId)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const payAtPropertyEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croPayAtProperty)
  const searchMpvSalesRankingV4Enabled = !!useOptimizelyExperiment(OptimizelyExperiments.searchMpvSalesRankingV4)
  const propertyNearbyGeoDistanceOnlyEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.searchPropertyNearbyGeoDistanceOnly, !!filters.propertyId)

  useEffect(() => {
    if (searchParams?.searchItem?.searchType === 'destination') {
      dispatch(fetchSpecificPlaces(searchParams.searchItem.value))
    }
  }, [dispatch, searchParams.searchItem])

  useEffect(() => {
    if (!destinationId || destinationId === ANYWHERE_PLACE_ID) {
      return
    }

    dispatch(fetchPlaceById(destinationId))
  }, [dispatch, destinationId])

  useEffect(() => {
    if (destinationImageId || !filters.destinationId || filters.destinationId === ANYWHERE_PLACE_ID) {
      return
    }

    dispatch(fetchDestinationImageId(filters.destinationId))
  }, [dispatch, filters.destinationId, destinationImageId])

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical('hotels', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({ search: setManySearchParamValues(windowSearch, paramsToSet) })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobileScreen, toggleSearchVertical])
  const queryParams = useQueryParams()

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params) &&
    !validateLatLngSearchParams(params) &&
    !validateNaturalLanguageSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = match.url === location.pathname
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/hotels'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, match])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  const maldivesPlaceId = 'le_2723d092b63885e0d7c260cc007e8b9d'
  const propertySpecificSearch = !!filters.propertyId
  const maldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)

  const { tags: propertyTags, fetching: fetchingPropertyTags } = usePropertyTags(
    filters.propertyId,
    { disabled: !propertySpecificSearch },
  )
  const propertyNotLocationSensitive = useMemo(() =>
    !fetchingPropertyTags && !propertyTags?.includes('location sensitive'),
  [fetchingPropertyTags, propertyTags])

  const flexibleSearch = !!isFlexibleSearch
  const isAnytimeSearch = ((!filters?.checkIn && !filters?.checkOut) && !flexibleSearch)

  const showFlashInNearbyPropertySearch = propertySpecificSearch && !isAnytimeSearch && propertyNotLocationSensitive
  const searchLandmarkSearchFunctionScoreEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.searchLandmarkSearchFunctionScoreEnabled, searchType === 'landmark')

  const listFilters = useMemo((): App.OfferListFilters => {
    let offerTypes: Array<App.OfferListOfferType> = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }
    if (filters.offerTypes?.includes('rental')) {
      offerTypes = filters.offerTypes
    } else if (filters.offerTypes?.includes('hotel')) {
      offerTypes = without(offerTypes, OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      scheduleVisibilities: getsScheduleVisibilitiesFilter(isSpoofed),
      include: ['nearby'],
      source,
      isStream: true,
      rollout: [
        showFlashInNearbyPropertySearch ? 'showLosFlashInNearby' : undefined,
        payAtPropertyEnabled ? 'payAtPropertyEnabled' : undefined,
        searchMpvSalesRankingV4Enabled ? 'mpvSalesRanking' : undefined,
        propertyNearbyGeoDistanceOnlyEnabled ? 'propertyNearbyGeoDistanceOnly' : undefined,
        searchLandmarkSearchFunctionScoreEnabled ? 'landmarkAirportEnableFunctionScore' : undefined,
      ].filter(excludeNullOrUndefined),
    }
  }, [filters, searchType, isSpoofed, source, showFlashInNearbyPropertySearch, payAtPropertyEnabled, searchMpvSalesRankingV4Enabled, propertyNearbyGeoDistanceOnlyEnabled, searchLandmarkSearchFunctionScoreEnabled])

  const listKey = getOfferListKey(listFilters)

  const searchId = useMemo(() => {
    return uuidV4()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey])

  const globalHotelsSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (globalHotelsSearchDispatchRef.current) {
      globalHotelsSearchDispatchRef.current({
        type: GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem: propertySearchItem,
      })
    }

    if (isMobileScreen) {
      toggleSearchVertical('hotels', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  const utmBp = useAppSelector(state => state.utm.bp)
  const hideAnytimePrices = shouldHideAnytimePrices(isAnytimeSearch, utmBp)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    hideAnytimePrices,
  }, queryParams)

  useEffect(() => {
    globalHotelsSearchDispatchRef.current = globalHotelsSearchDispatch
  }, [globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePrices,
    })
  }, [hideAnytimePrices, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHotelsSearchDispatch])

  const destinationIds = useMemo(
    () => listFilters.destinationIds ?? (listFilters.destinationId ? [listFilters.destinationId] : undefined),
    [listFilters.destinationIds, listFilters.destinationId],
  )

  const overrideDestinationIds = useMemo<Array<string> | undefined>(() => {
    if (listFilters.propertyId) return [listFilters.propertyId]
    return destinationIds
  }, [listFilters.propertyId, destinationIds])

  const overrideDestinationType = useMemo<'location' | 'property' | undefined>(() => {
    if (listFilters.propertyId) return 'property'
    if (destinationIds) return 'location'
    return undefined
  }, [listFilters.propertyId, destinationIds])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      occupancies: listFilters.rooms,
      flexibleNights: listFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters.flexibleMonths,
      durationRange: listFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: listFilters.checkIn ? moment(listFilters.checkIn) : undefined,
      checkoutDate: listFilters.checkOut ? moment(listFilters.checkOut) : undefined,
      searchItem: searchParams?.searchItem,
      overrideDestinationIds,
      overrideDestinationType,
      eventAnalytics: { searchId },
    })
  }, [
    globalHotelsSearchState,
    listFilters,
    searchParams?.searchItem,
    searchId,
    overrideDestinationIds,
    overrideDestinationType,
  ])

  const filterContexts = useMemo(() => {
    return mapOfferListFiltersToFilterContexts({ offerListFilters: listFilters, CUSTOMER_RATING_GTE })
  }, [listFilters])

  const sortContext = useMemo(() => {
    return mapOfferListFiltersToSortContext({ offerListFilters: listFilters })
  }, [listFilters])

  const sortWasApplied = useSortWasApplied(listFilters)

  const filterWasApplied = useFilterWasApplied(listFilters)

  const trackingSearchEvent = useTrackingSearchEvent({
    searchSessionSearchEvent: previouslyBlockedSearch ? { type: 'search_bar' } : searchSessionSearchEvent,
    sortWasApplied,
    filterWasApplied,
  })

  const disableOfferList = (propertySpecificSearch && fetchingPropertyTags) || blockSearch

  const offerListFilters = useOfferListFilters(listFilters, { disabled: disableOfferList })

  const minRequiredOffers = useMemo(() => {
    return (parseInt(params.pages ?? '1') * pageSize) || undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.pages])

  const trackingOptions = useMemo<TrackingOptions>(() => (
    {
      searchEvent: trackingSearchEvent,
      searchContext,
      filterContexts,
      sortContext,
      searchSessionId,
    }
  ), [trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId])

  const hotelOfferList = useOfferList(listFilters, { disabled: disableOfferList, typeaheadId }, trackingOptions)

  const propertyOfferIds = useMemo(() => (
    propertySpecificSearch ? hotelOfferList.offerIds.slice(0, 1) : []
  ), [propertySpecificSearch, hotelOfferList.offerIds])
  const [propertySearchOffers] = useOffers<App.AnyOffer>(propertyOfferIds)
  const propertySearchOffer = propertySearchOffers[0]
  const propertyCountry = useMemo(() => {
    if (!propertySearchOffer) {
      return undefined
    }

    if (isBedbankOffer(propertySearchOffer)) {
      return propertySearchOffer.property.address.countryCode || propertySearchOffer.property.address.countryName
    }

    if (isHotel(propertySearchOffer)) {
      const geoCountry = propertySearchOffer.property?.geoData?.countryCode || propertySearchOffer.property?.geoData?.country
      if (geoCountry) {
        return geoCountry
      }
    }

    return undefined
  }, [propertySearchOffer])

  const searchListKey = hotelOfferList.key

  const propertyIdForRecentSearch = searchParams.searchItem?.searchType === 'searchItemV3' && 'property' in searchParams.searchItem.searchItem.extractedData
    ? searchParams.searchItem.searchItem.extractedData.property?.propertyId
    : searchParams.searchItem?.value
  const recentSearchOffer = useOffersWithProperty(hotelOfferList.offerIds, propertyIdForRecentSearch)
  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams

    if (searchItem && recentSearchesWhitelist.has(searchType)) {
      // Only add natural language searches to recent searches once extraction is complete
      const isNaturalLanguageSearch = searchItem.searchType === 'searchItemV3' &&
        searchItem.display.searchType === 'naturalLanguage'

      if (isNaturalLanguageSearch) {
        const isExtractionComplete = !searchItem.searchItem.extracting &&
          !!searchItem.searchItem.extractedData

        if (!isExtractionComplete) {
          return
        }
      }

      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }

      let recentSearchItem: App.SearchItem = searchItem

      // Handle V1 property search
      if (searchType === 'property' && recentSearchOffer) {
        recentSearchItem = createRecentPropertySearchObject(searchItem, recentSearchOffer)
      }

      // Handle V3 property search - enrich with offerId and imageId
      if (
        searchItem.searchType === 'searchItemV3' &&
        searchItem.display.searchType === 'property' &&
        recentSearchOffer
      ) {
        recentSearchItem = {
          ...searchItem,
          display: {
            ...searchItem.display,
            offerId: isLEHotel(recentSearchOffer) ? recentSearchOffer.id : undefined,
            imageId: recentSearchOffer.image.id,
          },
        }
      }

      dispatch(addRecentSearch(recentSearchItem, dates, rooms))
    }
  }, [dispatch, recentSearchOffer, searchParams, searchType])

  useEffect(() => {
    const placeId = params.destinationId ?? params.landmarkId
    if (placeId) {
      saveSearchedPlaces(placeId.split(','))
    }
  }, [params.destinationId, params.landmarkId])

  const deprecatedSearchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // eslint-disable-next-line
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const sortOptions = useMemo(() => {
    const options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    return [...options, SORT_OPTION_CUSTOMER_RATING]
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const showModal = useContext(ModalContext)
  const streamedExtras = useOfferListExtras(listFilters)
  const searchExpired = useStreamSearchExpired(listFilters)

  useEffect(() => {
    if (searchExpired) {
      Analytics.trackClientEvent({
        subject: 'expired',
        action: 'show',
        category: 'search',
        type: 'nonInteraction',
      })

      showModal(<RefreshSearchModal />)
    }
  }, [searchExpired, showModal])

  const destinationCountryMatchesMaldives = isMaldivesLocation(destinationPlace?.countryCode)
  const propertyCountryMatchesMaldives = propertySpecificSearch && isMaldivesLocation(propertyCountry)
  const isMaldivesDestination = destinationId === maldivesPlaceId

  // Check if any offers in the search results have the Maldives charter bundle campaign tag
  // This handles cases like "Central Atolls" which belong to Maldives but may not match destination checks
  const hasCharterBundleOffers = useAppSelector(state => {
    return hotelOfferList.offerIds.some(offerId => {
      const offer = state.offer.offers[offerId]
      if (!offer || !('campaignTags' in offer)) {
        return false
      }
      return offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
    })
  })

  const isMaldivesSearch = Boolean(
    isMaldivesDestination ||
    destinationCountryMatchesMaldives ||
    propertyCountryMatchesMaldives ||
    hasCharterBundleOffers,
  )

  const mainListFetching = hotelOfferList.fetching || blockSearch

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)
  const [overrideOfferListFetching, setOverrideOfferListFetching] = useState<boolean>(false)
  const [numberOfInitialLoadingTilesToDisplay, setNumberOfInitialLoadingTilesToDisplay] = useState<number>(() => {
    return (params.pages ? parseInt(params.pages) : 1) * pageSize
  })
  const [maldivesBundlePrice, setMaldivesBundlePrice] = useState<number>(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
  const [isMaldivesBundlePriceLoading, setIsMaldivesBundlePriceLoading] = useState<boolean>(false)

  // Calculate next valid charter period dates using hook
  const { fallbackCheckinDate, maldivesNights, effectiveReturnDate } = useNextValidCharterPeriod({
    checkIn: listFilters.checkIn,
    checkOut: listFilters.checkOut,
  })

  // Create bundle version of windowSearch with fallback checkIn and checkOut dates
  const bundleWindowSearch = useMemo(() => {
    if (!fallbackCheckinDate || !effectiveReturnDate) {
      return windowSearch
    }

    const paramsToSet = [
      { paramName: 'checkIn', paramValue: fallbackCheckinDate },
      { paramName: 'checkOut', paramValue: effectiveReturnDate },
    ]

    // Add bundle=12-nights parameter only when maldivesNights is 12
    if (maldivesNights === 12) {
      paramsToSet.push({ paramName: 'bundle', paramValue: '12-nights' })
    }

    return setManySearchParamValues(windowSearch, paramsToSet)
  }, [windowSearch, fallbackCheckinDate, effectiveReturnDate, maldivesNights])

  // Create bundle filters with fallback dates for price calculation
  // Sort by price ascending so first offer is the cheapest
  const bundleFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!isMaldivesSearch || !maldivesCharterEnabled || !fallbackCheckinDate || !effectiveReturnDate) {
      return undefined
    }

    return {
      ...listFilters,
      checkIn: fallbackCheckinDate,
      checkOut: effectiveReturnDate,
      campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
      sortBy: 'price.asc',
    }
  }, [isMaldivesSearch, maldivesCharterEnabled, listFilters, fallbackCheckinDate, effectiveReturnDate])

  const isBundleMode = isMaldivesSearch && maldivesCharterEnabled

  // Calculate total travellers for bundle pricing and banner visibility
  const totalTravellers = useMemo(() => listFilters.rooms ? sumUpOccupancies(listFilters.rooms) : 0, [listFilters.rooms])
  const isBelowMaxTravellers = totalTravellers <= MAX_TRAVELLERS_FOR_MALDIVES_CHARTER

  // Check if user's check-in date is within valid charter period
  const isCheckInWithinCharterPeriod = useMemo(() => {
    if (!listFilters.checkIn) return true // if no check-in date, we assume it's within the charter period
    return listFilters.checkIn >= MALDIVES_CHARTER_SEARCH_PAGE_MIN_CHECK_IN_DATE &&
      listFilters.checkIn <= MALDIVES_CHARTER_SEARCH_PAGE_MAX_CHECK_IN_DATE
  }, [listFilters.checkIn])

  // Filter out charter bundle offers that are not the exact property searches when:
  // - Over max travellers
  // OR
  // - Check-in date is outside valid charter period
  const charterExcludedOfferIds = useAppSelector(state => {
    if (isBelowMaxTravellers && isCheckInWithinCharterPeriod) {
      return undefined
    }

    const indexOfNearbySeparator = hotelOfferList.extras?.find((extra: App.OfferListExtra) => {
      return extra.type === 'nearby'
    })?.position

    // Filter offers that have charter bundle route enabled
    return hotelOfferList.offerIds.filter((offerId, index) => {
      if (propertySpecificSearch && !!indexOfNearbySeparator && index < indexOfNearbySeparator) {
        return false // We do not want to hide the property that the customer specifically searched for
      }

      const offer = state.offer.offers[offerId]
      if (!offer) return false

      return !!getCharterBundleRoute(state, offer)
    })
  })

  // Check if user's dates exactly match the bundle dates (no separate bundle search needed)
  const isDateMatchingBundlePeriod = listFilters.checkIn === fallbackCheckinDate &&
    listFilters.checkOut === effectiveReturnDate

  // Fetch bundle offer list data in HotelSearchPage
  // This ensures all bundle data requests are centralized here
  // Skip if user's dates exactly match bundle dates (main search already has correct prices)
  const shouldFetchBundleData = !!(isBundleMode &&
    bundleFilters?.checkIn &&
    bundleFilters.checkOut &&
    isCheckInWithinCharterPeriod &&
    !isDateMatchingBundlePeriod)

  // Fetch bundle offer list data for price calculation
  // Note: This is used internally for bundle price calculation, not displayed directly
  const bundleOfferList = useOfferList(bundleFilters, {
    disabled: !shouldFetchBundleData || !isBelowMaxTravellers,
    minRequiredOffers: minRequiredOffers,
  })

  // Get cheapest offer (first one since list is sorted by price.asc)
  const [cheapestOffers] = useOffers<App.HotelOffer>(bundleOfferList.offerIds.slice(0, 1))
  const cheapestOffer = cheapestOffers[0]

  const onPageChange = useCallback((page: number) => {
    // Always use windowSearch for pagination to preserve user-selected dates
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page), hash: currentPageHash }))
  }, [windowSearch, currentPageHash, dispatch])

  const handleViewBundlesClick = useCallback(() => {
    // Navigate to Maldives charter page with bundle dates
    dispatch(pushWithRegion(`/campaign/${CHARTER_PAGE_SLUG}`, bundleWindowSearch))
  }, [dispatch, bundleWindowSearch])

  // Get charter flight price for Maldives bundle (Melbourne-Maldives route)
  const [flightPrice, flightPriceLoading] = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate: fallbackCheckinDate,
    checkOutDate: effectiveReturnDate,
    occupancies: filters.rooms ?? [],
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: !isMaldivesSearch || !maldivesCharterEnabled || !fallbackCheckinDate,
  })

  // Reset price when search params change
  useEffect(() => {
    setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
    setIsMaldivesBundlePriceLoading(true)
  }, [listFilters.checkIn, listFilters.rooms, params.destinationId, params.propertyId])

  // Calculate bundle price when both flight and cheapest hotel are available
  useEffect(() => {
    if (!isMaldivesSearch || !maldivesCharterEnabled || !fallbackCheckinDate || !effectiveReturnDate) {
      setIsMaldivesBundlePriceLoading(false)
      return
    }

    // Set loading state
    setIsMaldivesBundlePriceLoading(flightPriceLoading || bundleOfferList.fetching)

    // Calculate price when everything is ready
    if (!flightPriceLoading && !bundleOfferList.fetching && cheapestOffer && listFilters.rooms) {
      // Find package matching the selected nights (5 or 12)
      const pkg = cheapestOffer.packages.find((p) => p.duration === maldivesNights)

      if (pkg) {
        // Use memberPrice (discounted price), fall back to price
        // package.price is per room, so we need to multiply by number of rooms
        const numberOfRooms = listFilters.rooms.length
        const hotelPackagePricePerRoom = pkg.memberPrice || pkg.price
        const hotelPackagePrice = hotelPackagePricePerRoom * numberOfRooms

        // Calculate per person price
        // - flightPrice: total for all travellers
        // - hotelPackagePrice: total for all rooms
        // - perPersonPrice: (flightPrice + hotelPackagePrice) / totalTravellers
        const totalBundlePrice = flightPrice + hotelPackagePrice
        const perPersonPrice = totalTravellers > 0 ? totalBundlePrice / totalTravellers : totalBundlePrice

        // Use calculated price only if both flight and hotel prices are valid (> 0)
        if (perPersonPrice > 0 && flightPrice > 0) {
          setMaldivesBundlePrice(perPersonPrice)
        } else {
          setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
        }
      } else {
        setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
      }
    }
  }, [
    isMaldivesSearch,
    maldivesCharterEnabled,
    flightPrice,
    flightPriceLoading,
    bundleOfferList.fetching,
    fallbackCheckinDate,
    effectiveReturnDate,
    listFilters.rooms,
    cheapestOffer,
    maldivesNights,
    totalTravellers,
  ])

  const [headerRef, isInView] = useInView()

  const onEndReached = useCallback(() => {
    setShowFooter(true)
  }, [])

  useEffect(() => {
    setShowFooter(false)
  }, [searchKey])

  useEffect(() => {
    // when the search key changes and the main list is not fetching, we scroll the user to the top of the page
    if (mainListFetching) {
      setOverrideOfferListFetching(true)
      return
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [searchKey, mainListFetching])

  useEffect(() => {
    if (!mainListFetching && isInView) {
      setOverrideOfferListFetching(false)
    }
  }, [isInView, mainListFetching])

  useEffect(() => {
    /**
     * Updates the number of loading tiles to display for the next search when:
     * 1. The current search has completed (not fetching)
     * 2. The pages parameter has changed
     *
     * Logic:
     * - Calculate tiles based on current page number * page size
     * - Cap at maximum of 10 tiles to prevent excessive loading states
     */
    if (!mainListFetching && !overrideOfferListFetching) {
      const pageNumber = params.pages ? parseInt(params.pages) : 1
      const maxNumberOfTilesToShow = 10

      setNumberOfInitialLoadingTilesToDisplay(
        Math.min(pageNumber * pageSize, maxNumberOfTilesToShow),
      )
    }
  }, [mainListFetching, overrideOfferListFetching, pageSize, params.pages])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const mainListTotal = hotelOfferList.offerCount ?? 0
  // Adjusted total for display, excluding charter offers when over max travellers
  const displayListTotal = mainListTotal - (charterExcludedOfferIds?.length ?? 0)
  const totalOffersForMeta = mainListTotal || hotelOfferList.offerIds?.length || 0

  const searchHeadMeta = useMemo(() => getAccommodationSearchHeadMeta({
    searchItem: searchParams.searchItem,
    destinationId: listFilters.destinationId,
    destinationName: listFilters.destinationName,
    totalCount: totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  }), [
    searchParams.searchItem,
    listFilters.destinationId,
    listFilters.destinationName,
    totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  ])

  const canonical = useMemo(() => {
    if (searchParams.searchItem?.searchType !== 'destination') {
      return undefined
    }

    return getDestinationSearchCanonicalPath(destinationPlace)
  }, [searchParams.searchItem?.searchType, destinationPlace])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && searchListKey === dispatchAction.key) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters: listFilters,
        searchItem: searchParams.searchItem,
        checkIn: listFilters.checkIn,
        checkOut: listFilters.checkOut,
        vertical: 'hotels',
        occupancies: listFilters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))
      Analytics.trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, filterContexts, sortContext },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'clicked',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      listDepth.current = Math.max(position + 1, listDepth.current)
      Analytics.trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, roomRateId, filterContexts, sortContext },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [
    deprecatedSearchSessionId,
    tracking.listName,
    tracking.listId,
    searchContext,
    searchListKey,
    listFilters,
    searchParams.searchItem,
    filterContexts,
    sortContext,
  ])

  const isZSR = (
    destinationTypes.has(searchType) &&
    mainListTotal === 0 && !mainListFetching
  )

  const enabledFilters = useMemo((): EnabledFilters => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: searchTargetLabel == ANYWHERE_NAME,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
      agentHubFeatures: isAgentHubEnabled,
      loyalty: isCurrentRegionUserLoyaltyRegion,
      bedrooms: true,
      beds: true,
      paymentOptions: payAtPropertyEnabled,
      chains: true,
    }
  ), [searchTargetLabel, luxPlusEnabled, isCurrentRegionUserLoyaltyRegion, payAtPropertyEnabled])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0

  if (invalidSearchParams) {
    return null
  }

  const positionOfFirstSeparator = streamedExtras.length > 0 ? streamedExtras[0].position : undefined

  const noExactMatchesForPropertySearch = !mainListFetching && positionOfFirstSeparator === 0

  const showFilterPane = isDesktop &&
    (mainListTotal > 0 ||
      mainListFetching ||
      hasFiltersApplied ||
      ((searchType === 'property' || listFilters?.propertyId) && !noExactMatchesForPropertySearch)
    )

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: intl.formatMessage({
        id: '3HAXL_T9o9_xuqIkoF_O',
        defaultMessage: 'Map view',
        description: 'Form field label for switch to map view on hotel search page',
      }),
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters: listFilters,
  }

  const sortByPrice = listFilters.sortBy === 'price.asc' || listFilters.sortBy === 'price.desc'
  const sortByValue = listFilters.sortBy === 'value'
  const showLoadingAnimation = mainListFetching && !isAnytimeSearch && (sortByPrice || sortByValue)

  const numberOfInvalidOffers = hotelOfferList.numberOfInvalidOffers
  const numberOfValidOffers = hotelOfferList.numberOfValidOffers

  const isLifestyleBrand = isWhitelabelBrand(config.BRAND)
  const showMaldivesTopBanner = isMaldivesSearch && maldivesCharterEnabled && !propertySpecificSearch && isBelowMaxTravellers && !isLifestyleBrand
  const showMaldivesNearbyBanner = propertySpecificSearch && isMaldivesSearch && maldivesCharterEnabled && isBelowMaxTravellers && !isLifestyleBrand

  const streamedExtraElements = streamedExtras.reduce<Array<OfferListV2Item>>((items, extra) => {
    items.push({
      id: extra.id,
      position: extra.position,
      element: (
        <HeadingWrapper>
          <Heading variant="heading4">{extra.heading}</Heading>
        </HeadingWrapper>
      ),
      show: 'hasOffers' as const,
    })

    if (showMaldivesNearbyBanner && extra.id === 'nearby-offers') {
      items.push({
        id: 'maldives-banner-nearby',
        position: extra.position,
        element: <MaldivesBanner
          onViewBundlesClick={handleViewBundlesClick}
          price={maldivesBundlePrice}
          nights={maldivesNights}
          isPriceLoading={isMaldivesBundlePriceLoading}
        />,
        show: 'hasOffers' as const,
      })
    }

    return items
  }, [])

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: (noExactMatchesForPropertySearch || isZSR),
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={displayListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={flexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane && !offerListFilters.fetching}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      query={params.query}
      numberOfInvalidOffers={numberOfInvalidOffers}
      numberOfValidOffers={numberOfValidOffers}
      searchType={listFilters.propertyId ? 'property' : searchType}
      enabledFilters={enabledFilters}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />,
    show: (!isZSR),
  },
  {
    id: 'promotional-banner',
    position: 'start',
    element: destinationId ? <ContentTagBanner
      page="search"
      destinationId={destinationId}
    /> : null,
    show: !isZSR && (destinationTags.length > 0 || hasOfferTagsApplied),
  },
  {
    id: 'loading-animation',
    position: 'start',
    element: <LoadingAnimationBox variant="hotels" />,
    show: showLoadingAnimation,
  },
  {
    id: 'scrolling-loading',
    position: 'start',
    element: <>
      {Array.from({ length: numberOfInitialLoadingTilesToDisplay || 4 }).map((_, index) => (
        <OfferListLoadingOfferTile
          key={`loading-tile-${index}`}
          tileStyle="search"
          offerType="hotel"
          className="offer-tile"
          filters={listFilters}
          offerPosition={index}
        />
      ))}
    </>,
    show: overrideOfferListFetching ? 'always' : false,
  },
  ...streamedExtraElements,
  {
    id: 'maldives-banner',
    position: 0,
    element: <MaldivesBanner
      onViewBundlesClick={handleViewBundlesClick}
      price={maldivesBundlePrice}
      nights={maldivesNights}
      isPriceLoading={isMaldivesBundlePriceLoading}
    />,
    show: showMaldivesTopBanner,
  },
  {
    id: 'trust-indicators',
    position: 2,
    element: <SearchPageTrustIndicators />,
    show: 'hasOffers' as const,
  },
  ]

  return <PageWrapper direction="vertical" verticalAlign="space-between" gap={16}>
    <div>
      <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
        <GlobalSearchContextProvider
          state={globalHotelsSearchState}
          dispatch={globalHotelsSearchDispatch}
        >
          <HeadData
            noIndex
            title={searchHeadMeta.title}
            description={searchHeadMeta.description}
            imageId={searchHeadMeta.imageId}
            imageUrl={searchHeadMeta.imageUrl}
            imageAlt={searchHeadMeta.imageAlt}
            canonical={canonical}
          />
          {shouldShowBanner && <SiteTakeoverSearchBanner vertical="hotel" />}
          <div ref={headerRef} />
          <HotelSearchHeader
            filters={listFilters}
            formRef={formRef}
            offerListFiltersProps={offerListFiltersProps}
            searchVertical="hotels"
            baseSearchPath="/search"
            baseSearchMapPath="/search/map"
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            locationSearchPlaceholder={naturalLanguageSearch ? locationLabels.placeholderLabelNaturalLanguage : locationLabels.placeholderLabel}
            label={naturalLanguageSearch ? locationLabels.titleLabelNaturalLanguage : locationLabels.titleLabel}
            showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
          />
          <ErrorBoundary fallback={null}>
            <PromptCardSelector tripReengagement />
          </ErrorBoundary>
          <MaldivesBundleProvider value={{ bundleFilters: bundleFilters, isBundleMode }}>
            <StyledLayoutContainer
              size={showFilterPane ? 'xl' : 'lg'}
              className={clsx({ filterPane: showFilterPane, fullWidth: isMobileScreen })}
            >
              <StyledVerticalSpacer gap={16}>
                <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                  <OfferList
                    filters={listFilters}
                    interstitials={extras}
                    onPageChange={onPageChange}
                    tileStyle="search"
                    eagerLoadFirstImage
                    defaultPage={initialPage.current}
                    fullWidth={isMobileScreen}
                    pageSize={pageSize}
                    clientCheckAvailability
                    searchId={searchId}
                    showLoadingTiles={!showLoadingAnimation}
                    onEndReached={onEndReached}
                    overrideFetching={overrideOfferListFetching}
                    minRequiredOffers={minRequiredOffers}
                    disableUseOfferList={disableOfferList}
                    excludedIds={charterExcludedOfferIds}
                    typeaheadId={typeaheadId}
                  />
                </OfferListEventsProvider>
              </StyledVerticalSpacer>
              {showFilterPane && <HotelSearchPageFilterPane
                filters={listFilters}
                mapViewURL={mapViewURL}
                search={windowSearch}
                enabledFilters={enabledFilters}
                hotelOfferList={hotelOfferList}
                mainListFetching={mainListFetching}
                doNotScrollToTopOfSelection
                showLoadingSkeletonOnEachFilterSelection
                bedroomsFilterGroupAtBottom={isStandardOccupancy(listFilters)}
                onFilterChange={handleFilterChange}
              />}
            </StyledLayoutContainer>
          </MaldivesBundleProvider>
          <OfferSelectionStickyFooter />
        </GlobalSearchContextProvider>
      </AnalyticsPageContext.Provider>
    </div>
    {showFooter && <Footer />}
  </PageWrapper>
}

export default withRouter(connector(HotelSearchPage))
